import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Table,
  Tag,
  Badge,
  Input,
  Select,
  Tooltip,
  Drawer,
  Modal,
  Popconfirm,
  Tabs,
  Alert,
  Descriptions,
  Divider,
  message,
  Switch,
  Statistic,
  Progress,
  Empty
} from 'antd';
import {
  ApiOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  UserOutlined,
  LaptopOutlined,
  HistoryOutlined,
  BugOutlined,
  ThunderboltOutlined,
  CopyOutlined,
  DisconnectOutlined,
  SendOutlined,
  DashboardOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  DeleteOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { admin } from '../../services/api';
import { buildWsPrintUrl } from '../../services/wsPrintClient';
import './WebSocketDashboard.css';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const formatSeconds = (sec) => {
  if (sec == null || isNaN(sec)) return '-';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
};

const formatUptimeMs = (ms) => {
  if (!ms) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  return formatSeconds(totalSeconds);
};

export default function WebSocketDashboard() {
  const [activeTab, setActiveTab] = useState('clients');
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10); // segundos
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Dados principais
  const [wsStatus, setWsStatus] = useState(null);
  const [wsClients, setWsClients] = useState([]);
  const [wsHistory, setWsHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [wsErrors, setWsErrors] = useState([]);
  const [errorsTotal, setErrorsTotal] = useState(0);

  // Filtros
  const [clientSearch, setClientSearch] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [errorSearch, setErrorSearch] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');

  // Detalhes Drawer / Modal
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState('Desconectado pelo administrador');
  const [disconnectTarget, setDisconnectTarget] = useState(null);

  // Diagnóstico & Testes
  const [testUuid, setTestUuid] = useState('');
  const [pingLoading, setPingLoading] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [browserWsConnected, setBrowserWsConnected] = useState(false);
  const [browserWsMessages, setBrowserWsMessages] = useState([]);
  const browserWsRef = useRef(null);

  const timerRef = useRef(null);

  // Copiar para clipboard com feedback
  const handleCopy = (text, label = 'UUID') => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      message.success(`${label} copiado!`);
    }).catch(() => {
      message.info(`Texto: ${text}`);
    });
  };

  // Carregar status e clientes
  const fetchActiveClients = async () => {
    try {
      const [statusResp, clientsResp] = await Promise.all([
        admin.getWsStatus(),
        admin.getWsClients()
      ]);
      setWsStatus(statusResp);
      setWsClients(clientsResp?.clients || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Erro ao buscar status do WS:', err);
    }
  };

  // Carregar histórico
  const fetchHistory = async () => {
    try {
      const params = {
        limit: 150,
        status: historyStatusFilter !== 'all' ? historyStatusFilter : undefined,
        search: historySearch || undefined
      };
      const resp = await admin.getWsHistory(params);
      setWsHistory(resp?.history || []);
      setHistoryTotal(resp?.total || 0);
    } catch (err) {
      console.error('Erro ao buscar histórico do WS:', err);
    }
  };

  // Carregar erros
  const fetchErrors = async () => {
    try {
      const params = {
        limit: 150,
        errorType: errorTypeFilter !== 'all' ? errorTypeFilter : undefined,
        search: errorSearch || undefined
      };
      const resp = await admin.getWsErrors(params);
      setWsErrors(resp?.errors || []);
      setErrorsTotal(resp?.total || 0);
    } catch (err) {
      console.error('Erro ao buscar erros do WS:', err);
    }
  };

  // Atualizar tudo
  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchActiveClients(),
        fetchHistory(),
        fetchErrors()
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Ciclo inicial e auto-refresh
  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh && refreshInterval > 0) {
      timerRef.current = setInterval(() => {
        if (activeTab === 'clients') {
          fetchActiveClients();
        } else if (activeTab === 'history') {
          fetchHistory();
        } else if (activeTab === 'errors') {
          fetchErrors();
        }
      }, refreshInterval * 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, refreshInterval, activeTab, historyStatusFilter, historySearch, errorTypeFilter, errorSearch]);

  // Desconectar cliente
  const handleDisconnect = async (client) => {
    try {
      const payload = {
        connectionId: client.connectionId,
        uuid: client.uuid,
        reason: disconnectReason || 'Desconectado pelo administrador'
      };
      const res = await admin.disconnectWsClient(payload);
      message.success(res.message || 'Cliente desconectado com sucesso');
      setDisconnectModalVisible(false);
      fetchActiveClients();
      fetchHistory();
    } catch (err) {
      message.error(err.message || 'Erro ao desconectar cliente');
    }
  };

  // Ping em cliente específico
  const handlePing = async (client) => {
    setPingLoading(true);
    const startTime = performance.now();
    try {
      const res = await admin.sendWsPing({
        connectionId: client.connectionId,
        uuid: client.uuid
      });
      const latency = Math.round(performance.now() - startTime);
      message.success(`Ping enviado para ${client.nome || client.uuid}! (${latency}ms)`);
      setPingResult({
        target: client.uuid || client.nome,
        success: true,
        latency,
        time: new Date().toLocaleTimeString()
      });
    } catch (err) {
      message.error(err.message || 'Falha ao enviar ping');
      setPingResult({
        target: client.uuid || client.nome,
        success: false,
        error: err.message,
        time: new Date().toLocaleTimeString()
      });
    } finally {
      setPingLoading(false);
    }
  };

  // Limpar histórico de erros
  const handleClearErrors = async () => {
    try {
      await admin.clearWsErrors();
      message.success('Log de erros de WebSocket limpo com sucesso');
      fetchErrors();
      fetchActiveClients();
    } catch (err) {
      message.error(err.message || 'Erro ao limpar log de erros');
    }
  };

  // Exportar histórico para CSV
  const handleExportHistoryCSV = () => {
    if (!wsHistory || wsHistory.length === 0) {
      message.warning('Nenhum registro para exportar');
      return;
    }
    const headers = ['ID', 'UUID', 'Status', 'Usuario', 'Nome', 'Empresa', 'Dispositivo', 'IP', 'ConectadoEm', 'DesconectadoEm', 'DuracaoSegundos', 'CodigoDesconexao', 'Motivo'];
    const rows = wsHistory.map(h => [
      h.id,
      h.uuid || '',
      h.status,
      h.usuario || '',
      h.nome || '',
      h.empresa || '',
      h.computer || '',
      h.ip || '',
      h.connectedAt || '',
      h.disconnectedAt || '',
      h.durationSeconds ?? '',
      h.disconnectCode ?? '',
      (h.disconnectReason || '').replace(/"/g, '""')
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(r => r.map(val => `"${val}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ws_historico_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Histórico exportado em CSV com sucesso!');
  };

  // Testar conexão local via navegador
  const toggleBrowserWsTest = () => {
    if (browserWsConnected) {
      if (browserWsRef.current) {
        browserWsRef.current.close();
        browserWsRef.current = null;
      }
      setBrowserWsConnected(false);
      setBrowserWsMessages(prev => [{ time: new Date().toLocaleTimeString(), text: 'Desconectado do WebSocket de teste.' }, ...prev]);
    } else {
      try {
        const wsUrl = buildWsPrintUrl();
        setBrowserWsMessages(prev => [{ time: new Date().toLocaleTimeString(), text: `Tentando conectar em ${wsUrl}...` }, ...prev]);
        const ws = new WebSocket(wsUrl);
        browserWsRef.current = ws;

        ws.onopen = () => {
          setBrowserWsConnected(true);
          setBrowserWsMessages(prev => [{ time: new Date().toLocaleTimeString(), text: '✅ Conexão aberta com sucesso!' }, ...prev]);
          ws.send(JSON.stringify({
            headers: {
              id: 'admin_test_' + Math.random().toString(36).substring(2, 6),
              usuario: 'Admin Tester',
              nome: 'Painel Admin Web',
              computer: navigator.userAgent.substring(0, 30),
              server: false
            }
          }));
        };

        ws.onmessage = (event) => {
          setBrowserWsMessages(prev => [
            { time: new Date().toLocaleTimeString(), text: `📥 Recebido: ${event.data}` },
            ...prev.slice(0, 40)
          ]);
        };

        ws.onerror = (e) => {
          setBrowserWsMessages(prev => [{ time: new Date().toLocaleTimeString(), text: '❌ Erro no WebSocket de teste' }, ...prev]);
        };

        ws.onclose = (e) => {
          setBrowserWsConnected(false);
          setBrowserWsMessages(prev => [{ time: new Date().toLocaleTimeString(), text: `⚠️ Conexão encerrada (código ${e.code})` }, ...prev]);
        };
      } catch (err) {
        message.error(`Falha ao conectar: ${err.message}`);
      }
    }
  };

  // Filtragem de clientes ativos
  const filteredClients = useMemo(() => {
    return wsClients.filter(c => {
      // Filtro de tipo
      if (clientTypeFilter === 'printer' && !c.isPrinter) return false;
      if (clientTypeFilter === 'web' && c.isPrinter) return false;
      if (clientTypeFilter === 'paired' && !c.pareado) return false;

      // Filtro de busca
      if (clientSearch) {
        const s = clientSearch.toLowerCase();
        const match =
          (c.uuid && c.uuid.toLowerCase().includes(s)) ||
          (c.usuario && c.usuario.toLowerCase().includes(s)) ||
          (c.nome && c.nome.toLowerCase().includes(s)) ||
          (c.empresa && c.empresa.toLowerCase().includes(s)) ||
          (c.computer && c.computer.toLowerCase().includes(s)) ||
          (c.ip && c.ip.toLowerCase().includes(s)) ||
          (c.pareado && c.pareado.toLowerCase().includes(s));
        if (!match) return false;
      }
      return true;
    });
  }, [wsClients, clientTypeFilter, clientSearch]);

  // Colunas de Clientes Conectados
  const clientColumns = [
    {
      title: 'Status',
      key: 'status',
      width: 90,
      render: () => (
        <Space size={6}>
          <span className="ws-pulse-dot online" />
          <Tag color="success">OPEN</Tag>
        </Space>
      )
    },
    {
      title: 'Tipo',
      key: 'isPrinter',
      width: 140,
      render: (_, row) => (
        row.isPrinter ? (
          <Tag color="cyan" icon={<PrinterOutlined />}>Impressora / Server</Tag>
        ) : (
          <Tag color="blue" icon={<LaptopOutlined />}>Cliente Web</Tag>
        )
      )
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 130,
      render: (uuid) => (
        <Space>
          <span className="ws-table-uuid" onClick={() => handleCopy(uuid)}>
            {uuid || <Text type="secondary">Pendente</Text>}
          </span>
          {uuid && (
            <Tooltip title="Copiar UUID">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined style={{ fontSize: 12 }} />}
                onClick={() => handleCopy(uuid)}
              />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Usuário & Nome',
      key: 'usuario',
      render: (_, row) => (
        <Space orientation="vertical" size={2}>
          <Text strong>{row.nome || row.usuario || 'Anônimo'}</Text>
          {row.usuario && row.usuario !== row.nome && (
            <Text type="secondary" style={{ fontSize: 12 }}>({row.usuario})</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Empresa / Filial',
      dataIndex: 'empresa',
      key: 'empresa',
      render: (emp) => emp ? <Tag color="geekblue">{emp}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: 'Dispositivo / Versão',
      key: 'computer',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>{row.computer || '-'}</Text>
          {row.versao && <Text type="secondary" style={{ fontSize: 11 }}>v{row.versao}</Text>}
        </Space>
      )
    },
    {
      title: 'Pareado com',
      dataIndex: 'pareado',
      key: 'pareado',
      render: (pareado) => pareado ? (
        <Tag color="purple" icon={<ThunderboltOutlined />}>{pareado}</Tag>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>Não pareado</Text>
      )
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      render: (ip) => ip ? <Text code style={{ fontSize: 11 }}>{ip}</Text> : '-'
    },
    {
      title: 'Tempo Ativo',
      dataIndex: 'uptimeSeconds',
      key: 'uptimeSeconds',
      render: (sec, row) => (
        <Tooltip title={row.connectedAt ? `Conectado em: ${new Date(row.connectedAt).toLocaleString('pt-BR')}` : ''}>
          <Space size={4}>
            <ClockCircleOutlined style={{ color: '#1890ff' }} />
            <span>{formatSeconds(sec)}</span>
          </Space>
        </Tooltip>
      )
    },
    {
      title: 'Mensagens',
      dataIndex: 'messagesCount',
      key: 'messagesCount',
      align: 'center',
      render: (cnt) => <Badge count={cnt || 0} showZero overflowCount={9999} style={{ backgroundColor: '#108ee9' }} />
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      width: 170,
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Testar Ping">
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined style={{ color: '#fa8c16' }} />}
              onClick={() => handlePing(row)}
              loading={pingLoading}
            />
          </Tooltip>
          <Tooltip title="Inspecionar Metadados">
            <Button
              type="text"
              size="small"
              icon={<DashboardOutlined style={{ color: '#1890ff' }} />}
              onClick={() => setSelectedClient(row)}
            />
          </Tooltip>
          <Tooltip title="Desconectar Conexão">
            <Button
              type="text"
              danger
              size="small"
              icon={<DisconnectOutlined />}
              onClick={() => {
                setDisconnectTarget(row);
                setDisconnectModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Colunas do Histórico
  const historyColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (st, row) => (
        st === 'connected' ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>Ativo</Tag>
        ) : (
          <Tag color={row.disconnectCode && row.disconnectCode !== 1000 ? 'warning' : 'default'} icon={<CloseCircleOutlined />}>
            Fechado
          </Tag>
        )
      )
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      render: (uuid) => (
        <span className="ws-table-uuid" onClick={() => handleCopy(uuid)}>
          {uuid || <Text type="secondary">-</Text>}
        </span>
      )
    },
    {
      title: 'Usuário',
      key: 'usuario',
      render: (_, row) => (
        <div>
          <Text strong>{row.nome || row.usuario || 'Anônimo'}</Text>
          {row.isPrinter && <Tag color="cyan" style={{ marginLeft: 6, fontSize: 10 }}>Printer</Tag>}
        </div>
      )
    },
    {
      title: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
      render: (e) => e ? <Tag>{e}</Tag> : '-'
    },
    {
      title: 'Dispositivo',
      dataIndex: 'computer',
      key: 'computer',
      render: (c) => c || '-'
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      render: (ip) => ip ? <Text code style={{ fontSize: 11 }}>{ip}</Text> : '-'
    },
    {
      title: 'Conectou Em',
      dataIndex: 'connectedAt',
      key: 'connectedAt',
      render: (dt) => dt ? dayjs(dt).format('DD/MM/YYYY HH:mm:ss') : '-'
    },
    {
      title: 'Desconectou Em',
      dataIndex: 'disconnectedAt',
      key: 'disconnectedAt',
      render: (dt) => dt ? dayjs(dt).format('DD/MM/YYYY HH:mm:ss') : <Text type="secondary">Em andamento</Text>
    },
    {
      title: 'Duração',
      dataIndex: 'durationSeconds',
      key: 'durationSeconds',
      render: (sec, row) => {
        if (row.status === 'connected') {
          const liveSec = Math.floor((Date.now() - new Date(row.connectedAt).getTime()) / 1000);
          return <Tag color="blue">{formatSeconds(liveSec)} (ativo)</Tag>;
        }
        return <Text>{formatSeconds(sec)}</Text>;
      }
    },
    {
      title: 'Motivo / Código',
      key: 'disconnectReason',
      render: (_, row) => (
        row.status === 'disconnected' ? (
          <Space direction="vertical" size={0}>
            {row.disconnectCode != null && <Text type="secondary" style={{ fontSize: 11 }}>Código: {row.disconnectCode}</Text>}
            {row.disconnectReason && <Text style={{ fontSize: 12 }}>{row.disconnectReason}</Text>}
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        )
      )
    }
  ];

  // Colunas de Erros
  const errorColumns = [
    {
      title: 'Data & Hora',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      render: (ts) => dayjs(ts).format('DD/MM/YYYY HH:mm:ss')
    },
    {
      title: 'Tipo de Erro',
      dataIndex: 'errorType',
      key: 'errorType',
      width: 170,
      render: (type) => {
        let color = 'red';
        if (type === 'HEARTBEAT_TIMEOUT') color = 'orange';
        if (type === 'JSON_PARSE') color = 'purple';
        if (type === 'UNEXPECTED_CLOSE') color = 'volcano';
        return <Tag color={color} icon={<BugOutlined />}>{type}</Tag>;
      }
    },
    {
      title: 'Mensagem',
      dataIndex: 'message',
      key: 'message',
      render: (msg) => <Text strong>{msg}</Text>
    },
    {
      title: 'UUID / Usuário',
      key: 'client',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {row.uuid && <Text className="ws-table-uuid" onClick={() => handleCopy(row.uuid)}>{row.uuid}</Text>}
          {row.usuario && <Text type="secondary" style={{ fontSize: 11 }}>{row.usuario}</Text>}
        </Space>
      )
    },
    {
      title: 'Dispositivo / IP',
      key: 'device',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {row.computer && <Text style={{ fontSize: 12 }}>{row.computer}</Text>}
          {row.ip && <Text code style={{ fontSize: 11 }}>{row.ip}</Text>}
        </Space>
      )
    },
    {
      title: 'Detalhes',
      key: 'details',
      width: 100,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => setSelectedError(row)}
          disabled={!row.details}
        >
          Ver Dados
        </Button>
      )
    }
  ];

  return (
    <div className="ws-dashboard-container">
      {/* Header com Status do Servidor */}
      <div className="ws-dashboard-header">
        <div className="ws-header-title">
          <span className={`ws-pulse-dot ${wsStatus?.running ? 'online' : 'offline'}`} />
          <Title level={3}>
            WebSocket de Impressão & Clientes
          </Title>
          <Tag color={wsStatus?.running ? 'success' : 'error'}>
            {wsStatus?.running ? 'ONLINE' : 'OFFLINE'}
          </Tag>
          {wsStatus?.protocol && (
            <Tag color={wsStatus.protocol === 'https' ? 'green' : 'geekblue'}>
              {wsStatus.protocol.toUpperCase()} / PORTA {wsStatus.port}
            </Tag>
          )}
        </div>

        <Space wrap size="middle">
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>Auto-atualizar:</Text>
            <Switch
              checked={autoRefresh}
              onChange={setAutoRefresh}
              size="small"
            />
            {autoRefresh && (
              <Select
                value={refreshInterval}
                onChange={setRefreshInterval}
                size="small"
                style={{ width: 80 }}
              >
                <Option value={5}>5s</Option>
                <Option value={10}>10s</Option>
                <Option value={30}>30s</Option>
                <Option value={60}>60s</Option>
              </Select>
            )}
          </Space>

          <Button
            type="primary"
            icon={<ReloadOutlined spin={loading} />}
            onClick={refreshAll}
            loading={loading}
          >
            Atualizar Agora
          </Button>
        </Space>
      </div>

      {/* Alerta de Erros se houver erros recentes */}
      {wsErrors.length > 0 && (
        <Alert
          className="ws-error-alert"
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                Há <strong>{wsErrors.length}</strong> erro(s) ou avisos registrados no WebSocket.
              </span>
              <Button size="small" type="link" onClick={() => setActiveTab('errors')}>
                Ver Logs de Erro →
              </Button>
            </div>
          }
        />
      )}

      {/* Grid de KPIs / Métricas Modernas */}
      <div className="ws-stats-grid">
        <Card className="ws-stat-card" bodyStyle={{ padding: 18 }}>
          <div className="ws-stat-accent-bar blue" />
          <div className="ws-stat-content">
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Clientes Conectados</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff', marginTop: 4 }}>
                {wsClients.length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Sockets ativos neste momento
              </Text>
            </div>
            <div className="ws-stat-icon-wrapper blue">
              <ApiOutlined />
            </div>
          </div>
        </Card>

        <Card className="ws-stat-card" bodyStyle={{ padding: 18 }}>
          <div className="ws-stat-accent-bar green" />
          <div className="ws-stat-content">
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Impressoras Ativas</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a', marginTop: 4 }}>
                {wsStatus?.activePrintersCount ?? wsClients.filter(c => c.isPrinter).length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Servidores de impressão
              </Text>
            </div>
            <div className="ws-stat-icon-wrapper green">
              <PrinterOutlined />
            </div>
          </div>
        </Card>

        <Card className="ws-stat-card" bodyStyle={{ padding: 18 }}>
          <div className="ws-stat-accent-bar purple" />
          <div className="ws-stat-content">
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Usuários Únicos</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#722ed1', marginTop: 4 }}>
                {wsStatus?.activeUsersCount ?? new Set(wsClients.map(c => c.usuario).filter(Boolean)).size}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Operadores online
              </Text>
            </div>
            <div className="ws-stat-icon-wrapper purple">
              <UserOutlined />
            </div>
          </div>
        </Card>

        <Card className="ws-stat-card" bodyStyle={{ padding: 18 }}>
          <div className="ws-stat-accent-bar orange" />
          <div className="ws-stat-content">
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Total de Conexões</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#fa8c16', marginTop: 4 }}>
                {wsStatus?.totalConnectionsEver ?? wsHistory.length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Uptime: {formatUptimeMs(wsStatus?.uptimeMs)}
              </Text>
            </div>
            <div className="ws-stat-icon-wrapper orange">
              <HistoryOutlined />
            </div>
          </div>
        </Card>

        <Card className="ws-stat-card" bodyStyle={{ padding: 18 }}>
          <div className="ws-stat-accent-bar red" />
          <div className="ws-stat-content">
            <div>
              <Text type="secondary" style={{ fontSize: 13 }}>Erros / Falhas</Text>
              <div style={{ fontSize: 28, fontWeight: 700, color: wsErrors.length > 0 ? '#f5222d' : '#8c8c8c', marginTop: 4 }}>
                {wsStatus?.totalErrorsEver ?? wsErrors.length}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {wsErrors.length} registrados no log
              </Text>
            </div>
            <div className="ws-stat-icon-wrapper red">
              <BugOutlined />
            </div>
          </div>
        </Card>
      </div>

      {/* Card Principal com Abas */}
      <Card className="ws-main-card">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'clients',
              label: (
                <Space>
                  <ApiOutlined />
                  <span>Clientes Conectados Agora ({wsClients.length})</span>
                </Space>
              ),
              children: (
                <div>
                  {/* Barra de Filtros */}
                  <div className="ws-filter-bar">
                    <Space wrap>
                      <Input.Search
                        placeholder="Buscar por UUID, usuário, IP, máquina..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        style={{ width: 280 }}
                        allowClear
                      />
                      <Select
                        value={clientTypeFilter}
                        onChange={setClientTypeFilter}
                        style={{ width: 180 }}
                      >
                        <Option value="all">Todos os tipos ({wsClients.length})</Option>
                        <Option value="printer">Apenas Impressoras ({wsClients.filter(c => c.isPrinter).length})</Option>
                        <Option value="web">Apenas Clientes Web ({wsClients.filter(c => !c.isPrinter).length})</Option>
                        <Option value="paired">Pareados ({wsClients.filter(c => c.pareado).length})</Option>
                      </Select>
                    </Space>

                    <Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Atualizado às: {lastUpdated.toLocaleTimeString()}
                      </Text>
                    </Space>
                  </div>

                  {/* Tabela de Clientes */}
                  <Table
                    dataSource={filteredClients}
                    columns={clientColumns}
                    rowKey={(r) => r.connectionId || r.uuid || `${r.ip}-${r.computer}`}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} clientes` }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    locale={{
                      emptyText: (
                        <Empty
                          description="Nenhum cliente conectado no momento"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              )
            },
            {
              key: 'history',
              label: (
                <Space>
                  <HistoryOutlined />
                  <span>Histórico de Conexões ({historyTotal || wsHistory.length})</span>
                </Space>
              ),
              children: (
                <div>
                  {/* Filtro do Histórico */}
                  <div className="ws-filter-bar">
                    <Space wrap>
                      <Input.Search
                        placeholder="Filtrar histórico por UUID, usuário, IP..."
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        onSearch={fetchHistory}
                        style={{ width: 280 }}
                        allowClear
                      />
                      <Select
                        value={historyStatusFilter}
                        onChange={(val) => {
                          setHistoryStatusFilter(val);
                        }}
                        style={{ width: 170 }}
                      >
                        <Option value="all">Todos os status</Option>
                        <Option value="connected">Apenas Conectados</Option>
                        <Option value="disconnected">Apenas Desconectados</Option>
                      </Select>
                      <Button icon={<ReloadOutlined />} onClick={fetchHistory}>
                        Filtrar
                      </Button>
                    </Space>

                    <Space>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExportHistoryCSV}
                      >
                        Exportar CSV
                      </Button>
                    </Space>
                  </div>

                  <Table
                    dataSource={wsHistory}
                    columns={historyColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} registros no histórico` }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    locale={{
                      emptyText: (
                        <Empty
                          description="Nenhum registro de histórico encontrado"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              )
            },
            {
              key: 'errors',
              label: (
                <Space>
                  <BugOutlined />
                  <span>Erros de Conexão ({wsErrors.length})</span>
                  {wsErrors.length > 0 && <Badge count={wsErrors.length} style={{ backgroundColor: '#ff4d4f' }} />}
                </Space>
              ),
              children: (
                <div>
                  <div className="ws-filter-bar">
                    <Space wrap>
                      <Input.Search
                        placeholder="Buscar erro por mensagem, UUID, IP..."
                        value={errorSearch}
                        onChange={(e) => setErrorSearch(e.target.value)}
                        onSearch={fetchErrors}
                        style={{ width: 280 }}
                        allowClear
                      />
                      <Select
                        value={errorTypeFilter}
                        onChange={setErrorTypeFilter}
                        style={{ width: 200 }}
                      >
                        <Option value="all">Todos os tipos de erro</Option>
                        <Option value="SOCKET_ERROR">SOCKET_ERROR</Option>
                        <Option value="HEARTBEAT_TIMEOUT">HEARTBEAT_TIMEOUT</Option>
                        <Option value="JSON_PARSE">JSON_PARSE</Option>
                        <Option value="UNEXPECTED_CLOSE">UNEXPECTED_CLOSE</Option>
                        <Option value="DELIVERY_FAIL">DELIVERY_FAIL</Option>
                        <Option value="GENERAL_ERROR">GENERAL_ERROR</Option>
                      </Select>
                      <Button icon={<ReloadOutlined />} onClick={fetchErrors}>
                        Filtrar
                      </Button>
                    </Space>

                    <Space>
                      {wsErrors.length > 0 && (
                        <Popconfirm
                          title="Limpar todos os erros registrados?"
                          description="Isso apagará o log atual de erros de WebSocket."
                          onConfirm={handleClearErrors}
                          okText="Sim, limpar"
                          cancelText="Cancelar"
                          okButtonProps={{ danger: true }}
                        >
                          <Button danger icon={<DeleteOutlined />}>
                            Limpar Log de Erros
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>

                  <Table
                    dataSource={wsErrors}
                    columns={errorColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} erros` }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    locale={{
                      emptyText: (
                        <Empty
                          description="Nenhum erro registrado! Servidor operando de forma saudável."
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              )
            },
            {
              key: 'diagnostic',
              label: (
                <Space>
                  <ThunderboltOutlined />
                  <span>Diagnóstico & Testes</span>
                </Space>
              ),
              children: (
                <div>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <Card title="Teste de Conectividade do Navegador com /ws/print" size="small">
                        <Paragraph type="secondary">
                          Testa se o seu próprio navegador consegue estabelecer um handshake WebSocket com o backend na rota <code>/ws/print</code>.
                        </Paragraph>
                        <Space style={{ marginBottom: 16 }}>
                          <Button
                            type={browserWsConnected ? 'default' : 'primary'}
                            danger={browserWsConnected}
                            onClick={toggleBrowserWsTest}
                          >
                            {browserWsConnected ? 'Desconectar Teste' : 'Conectar ao WebSocket'}
                          </Button>
                          <Tag color={browserWsConnected ? 'success' : 'default'}>
                            {browserWsConnected ? 'Conectado como Cliente' : 'Desconectado'}
                          </Tag>
                        </Space>

                        <div className="ws-json-preview" style={{ height: 180 }}>
                          {browserWsMessages.length === 0 ? (
                            <Text type="secondary">Nenhum evento registrado. Clique em 'Conectar ao WebSocket'.</Text>
                          ) : (
                            browserWsMessages.map((m, idx) => (
                              <div key={idx} style={{ marginBottom: 4 }}>
                                <Text type="secondary">[{m.time}]</Text> {m.text}
                              </div>
                            ))
                          )}
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} md={12}>
                      <Card title="Ping Direto para Cliente / Impressora" size="small">
                        <Paragraph type="secondary">
                          Envie um ping com timestamp diretamente para qualquer UUID ou conexão de impressora aberta.
                        </Paragraph>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input
                            placeholder="Digite o UUID do cliente ou selecione"
                            value={testUuid}
                            onChange={(e) => setTestUuid(e.target.value)}
                            addonBefore="UUID Alvo"
                          />
                          <Select
                            placeholder="Ou selecione um cliente ativo"
                            style={{ width: '100%' }}
                            value={testUuid || undefined}
                            onChange={(val) => setTestUuid(val)}
                            allowClear
                          >
                            {wsClients.map(c => (
                              <Option key={c.uuid || c.connectionId} value={c.uuid || c.connectionId}>
                                {c.isPrinter ? '🖨️ ' : '💻 '} {c.nome || c.usuario || c.uuid} ({c.uuid || 'Sem UUID'})
                              </Option>
                            ))}
                          </Select>

                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => handlePing({ uuid: testUuid })}
                            loading={pingLoading}
                            disabled={!testUuid}
                          >
                            Disparar Ping de Teste
                          </Button>

                          {pingResult && (
                            <Alert
                              style={{ marginTop: 12 }}
                              type={pingResult.success ? 'success' : 'error'}
                              message={
                                pingResult.success
                                  ? `Ping respondido em ${pingResult.latency}ms para ${pingResult.target} às ${pingResult.time}`
                                  : `Falha no ping para ${pingResult.target}: ${pingResult.error}`
                              }
                              showIcon
                            />
                          )}
                        </Space>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Drawer de Detalhes do Cliente Conectado */}
      <Drawer
        title="Detalhes da Conexão WebSocket"
        width={520}
        open={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        extra={
          selectedClient && (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={() => handlePing(selectedClient)}
                loading={pingLoading}
              >
                Ping
              </Button>
              <Button
                danger
                size="small"
                icon={<DisconnectOutlined />}
                onClick={() => {
                  setDisconnectTarget(selectedClient);
                  setDisconnectModalVisible(true);
                }}
              >
                Desconectar
              </Button>
            </Space>
          )
        }
      >
        {selectedClient && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="UUID">
                <Space>
                  <Text copyable strong>{selectedClient.uuid || 'Não definido'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {selectedClient.isPrinter ? (
                  <Tag color="cyan" icon={<PrinterOutlined />}>Impressora Desktop / Servidor</Tag>
                ) : (
                  <Tag color="blue" icon={<LaptopOutlined />}>Cliente Web</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Usuário">{selectedClient.usuario || '-'}</Descriptions.Item>
              <Descriptions.Item label="Nome">{selectedClient.nome || '-'}</Descriptions.Item>
              <Descriptions.Item label="Empresa">{selectedClient.empresa || '-'}</Descriptions.Item>
              <Descriptions.Item label="Dispositivo / PC">{selectedClient.computer || '-'}</Descriptions.Item>
              <Descriptions.Item label="Versão">{selectedClient.versao || '-'}</Descriptions.Item>
              <Descriptions.Item label="Endereço IP">{selectedClient.ip || '-'}</Descriptions.Item>
              <Descriptions.Item label="Pareado com">
                {selectedClient.pareado ? <Tag color="purple">{selectedClient.pareado}</Tag> : 'Não'}
              </Descriptions.Item>
              <Descriptions.Item label="Conectado em">
                {selectedClient.connectedAt ? dayjs(selectedClient.connectedAt).format('DD/MM/YYYY HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tempo Ativo">{formatSeconds(selectedClient.uptimeSeconds)}</Descriptions.Item>
              <Descriptions.Item label="Última Atividade">
                {selectedClient.lastActivityAt ? dayjs(selectedClient.lastActivityAt).fromNow() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Mensagens Recebidas">{selectedClient.messagesCount || 0}</Descriptions.Item>
              <Descriptions.Item label="Erros Ocorridos">{selectedClient.errorsCount || 0}</Descriptions.Item>
              <Descriptions.Item label="ID Conexão Backend">{selectedClient.connectionId || '-'}</Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '12px 0' }}>Metadados Brutos (JSON)</Divider>
            <pre className="ws-json-preview">
              {JSON.stringify(selectedClient, null, 2)}
            </pre>
          </Space>
        )}
      </Drawer>

      {/* Modal de Detalhes de Erro */}
      <Modal
        title="Detalhes do Erro de Conexão"
        open={!!selectedError}
        onCancel={() => setSelectedError(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedError(null)}>
            Fechar
          </Button>
        ]}
      >
        {selectedError && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Tipo:</Text> <Tag color="red">{selectedError.errorType}</Tag>
            </div>
            <div>
              <Text strong>Horário:</Text> {dayjs(selectedError.timestamp).format('DD/MM/YYYY HH:mm:ss')}
            </div>
            <div>
              <Text strong>Mensagem:</Text> {selectedError.message}
            </div>
            {selectedError.uuid && (
              <div>
                <Text strong>UUID:</Text> {selectedError.uuid}
              </div>
            )}
            {selectedError.ip && (
              <div>
                <Text strong>IP:</Text> {selectedError.ip}
              </div>
            )}
            {selectedError.details && (
              <div>
                <Text strong>Detalhes Técnicos:</Text>
                <pre className="ws-json-preview" style={{ marginTop: 8 }}>
                  {typeof selectedError.details === 'string'
                    ? selectedError.details
                    : JSON.stringify(selectedError.details, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Modal>

      {/* Modal de Desconectar Cliente */}
      <Modal
        title="Confirmar Desconexão de Cliente"
        open={disconnectModalVisible}
        onCancel={() => {
          setDisconnectModalVisible(false);
          setDisconnectTarget(null);
        }}
        onOk={() => disconnectTarget && handleDisconnect(disconnectTarget)}
        okText="Desconectar Agora"
        okButtonProps={{ danger: true }}
      >
        {disconnectTarget && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              Tem certeza que deseja encerrar a conexão do cliente{' '}
              <strong>{disconnectTarget.nome || disconnectTarget.usuario || disconnectTarget.uuid}</strong>?
            </Paragraph>
            <div>
              <Text strong>Motivo do encerramento:</Text>
              <Input
                style={{ marginTop: 8 }}
                value={disconnectReason}
                onChange={(e) => setDisconnectReason(e.target.value)}
                placeholder="Ex: Manutenção, reinicialização ou desconexão administrativa"
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}
